import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BarChart3, Camera, Search } from "lucide-react";
import api from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";
import AdminStats from "@/components/admin/AdminStats";
import AdminOrdersBoard from "@/components/admin/AdminOrdersBoard";
import AdminOrderDrawer from "@/components/admin/AdminOrderDrawer";
import AdminUsersTable from "@/components/admin/AdminUsersTable";
import AdminUserDrawer from "@/components/admin/AdminUserDrawer";
import useAdminData from "@/hooks/useAdminData";
import { extractError, formatDate, getOrderId, isUrgent, totalCredits } from "@/components/admin/adminHelpers";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { orders, subscriptions, users, notifications, adminStats, loading, setNotifications, fetchData } = useAdminData();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  const [focusedStatus, setFocusedStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsModal, setCreditsModal] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState(1);
  const [deliveryData, setDeliveryData] = useState({ analysis_link: "", message: "" });

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate("/dashboard");
  }, [user, authLoading, navigate]);

  const refreshData = useCallback(
    (options) => fetchData(options).catch((err) => {
      toast.error(extractError(err, "Erreur lors du chargement des données admin"));
      return null;
    }),
    [fetchData]
  );

  useEffect(() => {
    if (user?.role === "admin") refreshData();
  }, [user, refreshData]);

  const findOrder = useCallback((data, orderId) => data?.orders?.find((order) => getOrderId(order) === orderId || order.order_id === orderId), []);
  const findUser = useCallback((data, userId) => data?.users?.find((candidate) => candidate.id === userId), []);

  const runMutation = async ({ request, verify, success, error }) => {
    setSubmitting(true);
    let requestError = null;
    try {
      await request();
    } catch (err) {
      requestError = err;
    }

    try {
      const nextData = await fetchData({ silent: true });
      const verified = verify ? verify(nextData) : !requestError;
      if (!requestError || verified) {
        toast.success(success);
        return { ok: true, data: nextData };
      }
      toast.error(extractError(requestError, error));
      return { ok: false, data: nextData };
    } catch (refreshError) {
      toast.error(extractError(requestError || refreshError, error));
      return { ok: false, data: null };
    } finally {
      setSubmitting(false);
    }
  };

  const openOrder = (order) => {
    setSelectedOrder(order);
    setDeliveryData({ analysis_link: order.analysis_link || "", message: order.analyst_message || order.message || "" });
  };

  const handleStatusChange = async (orderId, status) => {
    const result = await runMutation({
      request: () => api.put(`/admin/orders/${orderId}/status`, { status }),
      verify: (data) => findOrder(data, orderId)?.status === status,
      success: "Statut mis à jour",
      error: "Impossible de mettre à jour le statut.",
    });
    if (result.ok) setSelectedOrder(findOrder(result.data, orderId) || null);
  };

  const handleTogglePriority = async (orderId, priority) => {
    const result = await runMutation({
      request: () => api.put(`/admin/orders/${orderId}/priority`, { priority }),
      verify: (data) => findOrder(data, orderId)?.priority === priority,
      success: priority ? "Commande mise en priorité" : "Priorité retirée",
      error: "Impossible de modifier la priorité.",
    });
    if (result.ok) setSelectedOrder(findOrder(result.data, orderId) || null);
  };

  const handleDeliver = async (orderId) => {
    const expectedLink = deliveryData.analysis_link.trim();
    if (!expectedLink) {
      toast.error("Ajoute le lien de livraison avant d'envoyer.");
      return;
    }

    const result = await runMutation({
      request: () => api.put(`/admin/orders/${orderId}/deliver`, { analysis_link: expectedLink, message: deliveryData.message || "" }),
      verify: (data) => {
        const order = findOrder(data, orderId);
        return order?.status === "delivered" && Boolean(order?.analysis_link);
      },
      success: "Analyse livrée",
      error: "La livraison a peut-être été enregistrée, mais la confirmation a échoué. Actualise la page pour vérifier.",
    });
    if (result.ok) {
      setSelectedOrder(findOrder(result.data, orderId) || null);
      setDeliveryData({ analysis_link: "", message: "" });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    const result = await runMutation({
      request: () => api.delete(`/admin/orders/${orderId}`),
      verify: (data) => !findOrder(data, orderId),
      success: "Commande supprimée",
      error: "Impossible de supprimer la commande.",
    });
    if (result.ok) setSelectedOrder(null);
  };

  const handleAddCredits = async (userId = creditsModal) => {
    if (!userId || creditsAmount < 1) return;
    const beforeTotal = totalCredits(users.find((candidate) => candidate.id === userId));
    const result = await runMutation({
      request: () => api.post(`/admin/users/${userId}/add-credits`, { credits: creditsAmount }),
      verify: (data) => totalCredits(findUser(data, userId)) >= beforeTotal + creditsAmount,
      success: `${creditsAmount} crédit(s) ajouté(s)`,
      error: "Impossible d'ajouter les crédits.",
    });
    if (result.ok) {
      setCreditsModal(null);
      setCreditsAmount(1);
      if (selectedUser?.id === userId) setSelectedUser(findUser(result.data, userId) || null);
    }
  };

  const handleUpdateUser = async (userId, payload) => {
    const result = await runMutation({
      request: () => api.put(`/admin/users/${userId}`, payload),
      verify: (data) => Boolean(findUser(data, userId)),
      success: "Utilisateur mis à jour",
      error: "Impossible de modifier l'utilisateur.",
    });
    if (result.ok) setSelectedUser(findUser(result.data, userId) || null);
  };

  const handleRoleChange = async (userId, role) => {
    const result = await runMutation({
      request: () => api.put(`/admin/users/${userId}/role`, { role }),
      verify: (data) => findUser(data, userId)?.role === role,
      success: "Rôle mis à jour",
      error: "Impossible de modifier le rôle.",
    });
    if (result.ok) setSelectedUser(findUser(result.data, userId) || null);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur et ses commandes ?")) return;
    const result = await runMutation({
      request: () => api.delete(`/admin/users/${userId}`),
      verify: (data) => !findUser(data, userId),
      success: "Utilisateur supprimé",
      error: "Impossible de supprimer l'utilisateur.",
    });
    if (result.ok) setSelectedUser(null);
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`, {});
      setNotifications((current) => current.map((item) => item.notification_id === notificationId ? { ...item, read: true } : item));
    } catch (err) {
      toast.error(extractError(err, "Impossible de marquer la notification comme lue."));
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post("/notifications/read-all", {});
      setNotifications((current) => current.map((item) => ({ ...item, read: true })));
    } catch (err) {
      toast.error(extractError(err, "Impossible de marquer les notifications comme lues."));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const filteredOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return orders
      .filter((order) => !urgentOnly || isUrgent(order))
      .filter((order) => {
        if (!query) return true;
        return [order.order_id, order.user_name, order.user_email, order.order_type, order.status, order.whatsapp, order.specific_request, formatDate(order.created_at)]
          .some((value) => String(value || "").toLowerCase().includes(query));
      })
      .sort((a, b) => Number(isUrgent(b)) - Number(isUrgent(a)) || new Date(a.deadline || a.created_at) - new Date(b.deadline || b.created_at));
  }, [orders, searchQuery, urgentOnly]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter((candidate) => [candidate.first_name, candidate.last_name, candidate.email, candidate.club, candidate.role, candidate.whatsapp]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [users, searchQuery]);

  const stats = {
    pending: orders.filter((order) => order.status === "pending").length,
    progress: orders.filter((order) => order.status === "in_progress").length,
    delivered: orders.filter((order) => order.status === "delivered").length,
    urgent: orders.filter(isUrgent).length,
  };
  const unreadNotifications = notifications.filter((item) => !item.read).length;

  if (authLoading || !user || user.role !== "admin") {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <AdminHeader activeTab={activeTab} setActiveTab={setActiveTab} setFocusedStatus={setFocusedStatus} unreadNotifications={unreadNotifications} fetchData={refreshData} handleLogout={handleLogout} />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24"><div className="max-w-7xl mx-auto">
        <AdminStats stats={stats} setActiveTab={setActiveTab} setFocusedStatus={setFocusedStatus} />
        {adminStats && <section className="rounded-3xl bg-[#18181B] border border-white/10 p-5 mb-6"><div className="mb-4"><p className="text-xs text-zinc-500 uppercase">Pilotage</p><h2 className="text-xl font-black text-white uppercase flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#CCFF00]" />Statistiques business</h2></div><div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"><div className="bg-[#0A0A0A] border border-white/10 p-3"><p className="text-2xl font-black text-white">{adminStats.orders_this_month}</p><p className="text-xs text-zinc-500 uppercase">Commandes ce mois</p></div><div className="bg-[#0A0A0A] border border-white/10 p-3"><p className="text-2xl font-black text-green-400">{adminStats.delivered_this_month}</p><p className="text-xs text-zinc-500 uppercase">Livrées ce mois</p></div><div className="bg-[#0A0A0A] border border-white/10 p-3"><p className="text-2xl font-black text-blue-400">{adminStats.average_delivery_days}j</p><p className="text-xs text-zinc-500 uppercase">Délai moyen</p></div><div className="bg-[#0A0A0A] border border-white/10 p-3"><p className="text-2xl font-black text-[#CCFF00]">{adminStats.average_rating}/5</p><p className="text-xs text-zinc-500 uppercase">Avis moyen</p></div><div className="bg-[#0A0A0A] border border-white/10 p-3"><p className="text-2xl font-black text-white">{adminStats.estimated_revenue}€</p><p className="text-xs text-zinc-500 uppercase">CA estimé</p></div><div className="bg-[#0A0A0A] border border-white/10 p-3"><p className="text-2xl font-black text-white">{adminStats.active_subscriptions}</p><p className="text-xs text-zinc-500 uppercase">Abonnements</p></div></div></section>}
        <div className="flex flex-wrap items-center gap-2 mb-6"><button onClick={() => setActiveTab("orders")} className={`px-4 py-2 font-bold text-sm uppercase ${activeTab === "orders" ? "bg-[#CCFF00] text-black" : "bg-[#18181B] text-white"}`}>Commandes</button><button onClick={() => { setActiveTab("users"); setFocusedStatus(null); }} className={`px-4 py-2 font-bold text-sm uppercase ${activeTab === "users" ? "bg-[#CCFF00] text-black" : "bg-[#18181B] text-white"}`}>Utilisateurs</button><button onClick={() => { setActiveTab("subscriptions"); setFocusedStatus(null); }} className={`px-4 py-2 font-bold text-sm uppercase ${activeTab === "subscriptions" ? "bg-[#CCFF00] text-black" : "bg-[#18181B] text-white"}`}>Abonnements</button><button onClick={() => { setActiveTab("notifications"); setFocusedStatus(null); }} className={`px-4 py-2 font-bold text-sm uppercase ${activeTab === "notifications" ? "bg-[#CCFF00] text-black" : "bg-[#18181B] text-white"}`}>Notifications {unreadNotifications > 0 ? `(${unreadNotifications})` : ""}</button><button onClick={() => { setActiveTab("profile"); setFocusedStatus(null); }} className={`px-4 py-2 font-bold text-sm uppercase ${activeTab === "profile" ? "bg-[#CCFF00] text-black" : "bg-[#18181B] text-white"}`}>Profil</button></div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-6"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" /><Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="pl-10 bg-[#18181B] border-white/10 text-white placeholder:text-zinc-500" placeholder={activeTab === "orders" ? "Rechercher commande, email, nom, statut, date, WhatsApp, demande..." : "Rechercher email, nom, rôle, club, WhatsApp..."} /></div>{activeTab === "orders" && <label className="flex items-center gap-2 bg-[#18181B] border border-white/10 text-white px-4 py-2 text-sm"><input type="checkbox" checked={urgentOnly} onChange={(event) => setUrgentOnly(event.target.checked)} />Urgentes seulement</label>}</div>
        {loading ? <div className="text-center py-12"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin mx-auto" /></div> : activeTab === "orders" ? <AdminOrdersBoard orders={filteredOrders} focusedStatus={focusedStatus} setFocusedStatus={setFocusedStatus} openOrder={openOrder} /> : activeTab === "users" ? <AdminUsersTable users={filteredUsers} orders={orders} onOpenUser={setSelectedUser} onAddCredits={(userId) => { setCreditsModal(userId); setCreditsAmount(1); }} /> : activeTab === "notifications" ? <section className="bg-[#18181B] border border-white/10 rounded-3xl overflow-hidden"><div className="border-b border-white/10 p-5 flex items-center justify-between gap-3"><div><p className="text-xs text-zinc-500 uppercase">Notifications</p><h2 className="text-2xl font-black text-white uppercase">Historique</h2></div>{notifications.length > 0 && <button onClick={markAllNotificationsRead} className="bg-[#CCFF00] text-black px-4 py-2 text-xs font-bold uppercase hover:bg-[#AACC00]">Tout marquer lu</button>}</div><div className="divide-y divide-white/10">{notifications.length === 0 ? <p className="p-6 text-sm text-zinc-500">Aucune notification.</p> : notifications.map((item) => <button key={item.notification_id} type="button" onClick={() => { markNotificationRead(item.notification_id); const order = orders.find((candidate) => getOrderId(candidate) === item.order_id); if (order) openOrder(order); }} className={`w-full p-5 text-left hover:bg-white/[0.03] transition-colors ${item.read ? "opacity-60" : ""}`}><div className="flex items-start justify-between gap-4"><div><p className="text-white font-bold">{item.title}</p><p className="text-sm text-zinc-500 mt-1">{item.message}</p></div>{!item.read && <Badge className="bg-[#CCFF00] text-black">Nouveau</Badge>}</div></button>)}</div></section> : activeTab === "profile" ? <section className="rounded-3xl bg-[#18181B] border border-white/10 p-6 max-w-3xl"><div className="flex items-center gap-4 mb-6"><div className="h-20 w-20 rounded-2xl border border-white/10 bg-[#0A0A0A] overflow-hidden flex items-center justify-center">{user?.profile_photo_url ? <img src={user.profile_photo_url} alt={user.first_name} className="h-full w-full object-cover" /> : <Camera className="w-8 h-8 text-zinc-600" />}</div><div><p className="text-xs text-zinc-500 uppercase">Profil admin</p><h2 className="text-2xl font-black text-white">{user?.first_name} {user?.last_name}</h2><p className="text-sm text-zinc-500">{user?.job_title || "Admin SkillsView"}</p></div></div><p className="text-sm text-zinc-500">Pour modifier le profil admin, va dans l'onglet Utilisateurs puis ouvre ton compte.</p></section> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{subscriptions.length === 0 ? <p className="text-zinc-500">Aucun abonnement actif.</p> : subscriptions.map((sub) => <div key={sub.email} className="bg-[#18181B] border border-white/10 p-5 rounded-3xl"><p className="text-white font-bold">{sub.first_name} {sub.last_name}</p><p className="text-zinc-500 text-sm">{sub.email}</p><Badge className="mt-3 bg-[#CCFF00]/20 text-[#CCFF00]">{sub.subscription_credits || 0} crédit(s)</Badge></div>)}</div>}
      </div></main>
      <AdminOrderDrawer order={selectedOrder} onClose={() => setSelectedOrder(null)} onStatusChange={handleStatusChange} onDelete={handleDeleteOrder} onTogglePriority={handleTogglePriority} deliveryData={deliveryData} setDeliveryData={setDeliveryData} onDeliver={handleDeliver} submitting={submitting} />
      <AdminUserDrawer user={selectedUser} orders={orders} onClose={() => setSelectedUser(null)} onUpdateUser={handleUpdateUser} onRoleChange={handleRoleChange} onDelete={handleDeleteUser} onAddCredits={(userId) => { setCreditsModal(userId); setCreditsAmount(1); }} submitting={submitting} />
      {creditsModal && <div className="fixed inset-0 z-[90] flex items-center justify-center px-6"><button className="absolute inset-0 bg-black/70" onClick={() => setCreditsModal(null)} aria-label="Fermer" /><div className="relative bg-[#18181B] border border-white/10 p-6 w-full max-w-sm rounded-3xl"><h2 className="text-xl font-black uppercase text-white mb-4">Ajouter des crédits</h2><Label className="text-white text-sm">Nombre de crédits</Label><Input type="number" min="1" max="10" value={creditsAmount} onChange={(event) => setCreditsAmount(parseInt(event.target.value, 10) || 1)} className="mt-2 bg-[#0A0A0A] border-white/20 text-white" /><div className="flex gap-2 mt-5"><button onClick={() => handleAddCredits()} disabled={submitting} className="flex-1 bg-[#CCFF00] text-black py-3 font-bold uppercase text-sm hover:bg-[#AACC00] disabled:opacity-50">Ajouter</button><button onClick={() => setCreditsModal(null)} className="px-4 border border-white/20 text-white font-bold uppercase text-sm hover:bg-white/5">Annuler</button></div></div></div>}
    </div>
  );
};

export default AdminDashboard;
